import type { SxProps } from '@mui/material';
import { alpha, Box, ButtonBase, Typography } from '@mui/material';
import type { GallerySimple } from '@ocode/domain';
import { flatSx, StackRow } from '@ocodelib/mui';
import { formatByEpochSeconds } from '@ocodelib/util/date';
import clsx from 'clsx';
import { UserProfileDialogOpenEvent } from '../events/user-profile-open';
import { BbsUserBox } from './BbsUserBox';
import {
  CountWithBookmarkIcon,
  CountWithEyeIcon,
  CountWithHeartIcon,
  CountWithReplyIcon,
} from './count-with-icon';
import { GalleryListBreedTag } from './GalleryListBreedTag';

interface Props {
  sx?: SxProps;
  gallery: GallerySimple;
  onClick?: React.MouseEventHandler;
  userHidden?: boolean;
}

// js와 py는 썸네일이 없으므로 이미지 위치에 아이콘을 표시
const THUMBNAILS: Record<string, string> = {
  js: '/_static/images/ic_open_javascript.png',
  py: '/_static/images/ic_open_python.png',
  p5: '/_static/images/ic_open_p5js.png', // p5js는 썸네일이 있어서 이 아이콘은 사용되지 않는다.
};

/**
 * TODO:
 * P5, JS, PY 공용으로 변경할 것
 */
export function OpenLectureListRow(props: Props) {
  const { gallery, userHidden = false, onClick, sx } = props;
  const {
    breed,
    thumbUrl,
    title,
    introContent,
    seenCount,
    chimCount,
    favorCount,
    replyCount,
    author,
    isFavor,
    isChim,
    createTimestamp,
  } = gallery;

  const [createDateStr, createDate] = formatByEpochSeconds(createTimestamp!, 'YYYY.MM.DD');

  return (
    <Box className="OpenLectureListRow-root" sx={flatSx({ position: 'relative' }, sx)}>
      <ButtonBase
        component="div"
        disableRipple
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          border: '1px solid #ddd',
          borderRadius: '5px',
          overflow: 'hidden',
          // boxShadow: `1px 2px 4px rgba(0,0,0,0.25)`,
          '&:hover': {
            border: (theme) => `1px solid ${theme.palette.primary.main}`,
            background: (theme) => alpha(theme.palette.primary.main, 0.03),
          },
        }}
      >
        {/* grade tag */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            px: 1.5,
            py: 0.3,
            bgcolor: alpha('#039BE5', 0.75),
            color: '#fff',
            borderRadius: '5px 0 5px 0',
          }}
        >
          초급
        </Box>
        <Box
          className={clsx({
            hasThumb: !!thumbUrl,
          })}
          sx={{
            width: 160,
            height: 112,
            '& img': {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            },
            '&.hasThumb img': {
              objectFit: 'cover',
            },
          }}
        >
          {/* <NextImage src="/_static/images/ic_javascript.png" width={48} height={48} alt="js icon" /> */}
          <img src={thumbUrl ?? THUMBNAILS[breed]} width={160} height={112} alt="" />
        </Box>
        <Box
          className="OpenLectureListRow-right"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flex: 1,
            height: 112,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              minWidth: 200,
              height: '100%',
              py: 1.5,
              px: 3,
              '& .OpenLectureListRow-title': {
                color: '#222',
              },
              '& .OpenLectureListRow-desc': {
                mt: 1,
              },
            }}
          >
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              {/* tag */}
              <GalleryListBreedTag breed={breed} sx={{ mb: 0.5 }} />
              <Typography color="#76787A">{createDateStr}</Typography>
            </Box>

            <Box
              className="OpenLectureListRow-textBox-content"
              sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
            >
              <Box
                className="OpenLectureListRow-textBox"
                sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}
              >
                <Typography
                  sx={{
                    width: '90%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  variant="subtitle1"
                  className="OpenLectureListRow-title"
                >
                  {title}
                </Typography>
                <Typography
                  className="OpenLectureListRow-desc"
                  sx={{
                    width: '90%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {introContent}
                </Typography>
              </Box>

              <Box
                className="OpenLectureListRow-stampBox"
                sx={{
                  mt: 0.5,
                  width: 47,
                  height: 47,
                  '& img': {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  },
                }}
              >
                <img src="/_static/images/openlecture_stamp.png" />
              </Box>
            </Box>
          </Box>
        </Box>
      </ButtonBase>

      <Box
        className="OpenLectureListRow-bottom"
        sx={{
          display: 'flex',
          pr: 2,
          mt: 0.5,
          ml: 'auto',
          width: 265,
        }}
      >
        {!userHidden && (
          <Box sx={{ width: 120 }}>
            <BbsUserBox
              user={author}
              onClick={(e) => {
                e.stopPropagation();
                UserProfileDialogOpenEvent.emit(author.accountId);
              }}
              sx={{
                px: 1,
              }}
            />
          </Box>
        )}
        <StackRow alignItems="center" spacing={1} sx={{ color: '#777' }}>
          <CountWithEyeIcon count={seenCount} size="small" />
          <CountWithHeartIcon count={favorCount} size="small" active={isFavor} />
          <CountWithReplyIcon count={replyCount} size="small" />
          <CountWithBookmarkIcon count={chimCount} size="small" active={isChim} />
        </StackRow>
      </Box>
    </Box>
  );
}
